/**
 * shared/utils/searchParser.js (v2.71.62)
 * ---------------------------------------
 * Parse natural-language search queries into structured filters.
 *
 *   parseSearchQuery("3BHK Dwarka Mor", { locations, types })
 *   → {
 *       params: { bhk: "3 BHK", main_area: "Dwarka Mor" },
 *       label: "3 BHK · Dwarka Mor",
 *       parts: ["3 BHK", "Dwarka Mor"],
 *       residue: "",
 *     }
 *
 * Supported patterns:
 *   • BHK            "<N>bhk" / "<N> bhk" / "<N>-bhk"
 *   • Property type  "shop", "plot", "apartment", "flat", "house",
 *                    "builder floor", "office", "villa", "warehouse"
 *                    (matched against the types[] config too)
 *   • Budget         "under <N> lakh|cr|crore" / "below" / "less than" /
 *                    "upto" → max_price
 *                    "over <N> ..." / "above" / "more than" → min_price
 *                    "<N> lakh|cr|crore to <M> ..." → range
 *                    "₹<N>L-<M>Cr"
 *   • Location       remaining tokens matched against main_areas + sub_areas
 *
 * Backend untouched. Returns URL-param shape that the existing
 * FilterPills + Dashboard.jsx state already consumes.
 */

// ─── Vocabulary ─────────────────────────────────────────────────────

// Property type aliases → canonical type label (must match /api/filters options
// case-insensitively). The matcher cross-references against the live types[]
// passed in to handle CMS-renamed types gracefully.
// v2.87.x — Builder Floor is now the DEFAULT for "flat" / "apartment" /
// "floor" / "builder floor" search keywords. Real-society Apartment hits
// require the user to explicitly type "society", "DDA", "CGHS" etc.
const TYPE_ALIASES = {
  "flat":           "Builder Floor",
  "apartment":      "Builder Floor",
  "flat/apartment": "Builder Floor",
  "society":        "Builder Floor",
  "society flat":   "Builder Floor",
  "group housing":  "Builder Floor",
  "dda":            "Builder Floor",
  "dda flat":       "Builder Floor",
  "cghs":           "Builder Floor",
  "shop":           "Shop",
  "shops":          "Shop",
  "showroom":       "Shop",
  "plot":           "Plot",
  "plots":          "Plot",
  "land":           "Plot",
  "house":          "Independent House",
  "independent house": "Independent House",
  "kothi":          "Independent House",
  "villa":          "Independent House",
  "bungalow":       "Independent House",
  "builder floor":  "Builder Floor",
  "floor":          "Builder Floor",
  "office":         "Office",
  "office space":   "Office",
  "warehouse":      "Warehouse",
  "godown":         "Warehouse",
  "building":       "Building",
  "full building":  "Building",
};

const UNIT_MULTIPLIERS = {
  lakh:  100000,
  lac:   100000,
  lakhs: 100000,
  l:     100000,
  cr:    10000000,
  crore: 10000000,
  crores: 10000000,
};

// Strip leading "₹" and commas, normalize whitespace + case.
function normalize(q) {
  return (q || "")
    .replace(/[₹,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Parse a numeric+unit token e.g. "50 lakh", "1.5 cr" → 5000000 / 15000000.
function parseAmount(numStr, unitStr) {
  const n = parseFloat(numStr);
  if (!isFinite(n)) return null;
  const mult = UNIT_MULTIPLIERS[(unitStr || "").toLowerCase()] || 1;
  return Math.round(n * mult);
}

// ─── Sub-parsers — each returns {value, consumed} and never throws ─

function extractBhk(q) {
  // 3bhk · 3 bhk · 3-bhk · 3 b.h.k.
  const m = q.match(/(\d)\s*[- ]?b\.?h\.?k\.?/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (n < 1 || n > 10) return null;
  return {
    value: `${n} BHK`,
    raw: m[0],
  };
}

function extractType(q, typesConfig) {
  // Match against configured types FIRST, then aliases as fallback.
  const lower = q.toLowerCase();
  // Try longer multi-word phrases first ("builder floor" before "floor").
  const aliasKeys = Object.keys(TYPE_ALIASES).sort((a, b) => b.length - a.length);
  for (const alias of aliasKeys) {
    const re = new RegExp(`(^|\\s)${alias.replace(/\s+/g, "\\s+")}(?=\\s|$)`, "i");
    if (re.test(lower)) {
      const canonical = TYPE_ALIASES[alias];
      // If a CMS-configured type matches case-insensitively, return that
      // (preserves CMS casing); else return our canonical mapping.
      const exact = (typesConfig || []).find((t) => t.toLowerCase() === canonical.toLowerCase());
      return { value: exact || canonical, raw: alias };
    }
  }
  // Also try plain CMS types (covers types we don't have an alias for).
  for (const t of (typesConfig || [])) {
    if (!t) continue;
    const re = new RegExp(`(^|\\s)${t.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&")}(?=\\s|$)`, "i");
    if (re.test(lower)) return { value: t, raw: t.toLowerCase() };
  }
  return null;
}

function extractBudget(q) {
  // Patterns we accept (cleanest first):
  //   "under 50 lakh" / "below 1 cr" / "less than 2 cr" / "upto 50L"
  //   "over 1 cr" / "above 50 lakh" / "more than 1 cr"
  //   "50 lakh to 1 cr" / "50L - 1Cr" / "50L-1Cr"
  const unit = "(lakh|lac|lakhs|l|cr|crore|crores)";
  const num = "(\\d+(?:\\.\\d+)?)";

  // Range: "N unit to M unit" OR "N unit - M unit"
  const range = q.match(new RegExp(`${num}\\s*${unit}\\s*(?:to|-|–)\\s*${num}\\s*${unit}`, "i"));
  if (range) {
    const lo = parseAmount(range[1], range[2]);
    const hi = parseAmount(range[3], range[4]);
    if (lo != null && hi != null) {
      return { min: Math.min(lo, hi), max: Math.max(lo, hi), raw: range[0] };
    }
  }

  // Max: under / below / upto / less than
  const max = q.match(new RegExp(`(under|below|upto|less\\s+than|<)\\s*${num}\\s*${unit}`, "i"));
  if (max) {
    const m = parseAmount(max[2], max[3]);
    if (m != null) return { max: m, raw: max[0] };
  }

  // Min: over / above / more than
  const min = q.match(new RegExp(`(over|above|more\\s+than|>)\\s*${num}\\s*${unit}`, "i"));
  if (min) {
    const v = parseAmount(min[2], min[3]);
    if (v != null) return { min: v, raw: min[0] };
  }

  return null;
}

// Find a location match given a residue string.
// Returns {main_area, sub_area?, label, raw} or null.
function extractLocation(residue, locations) {
  if (!residue || !locations?.length) return null;
  const norm = residue.toLowerCase().trim();
  if (!norm) return null;

  // 1) Direct main-area match (start-with first, then substring)
  let bestMain = null;
  for (const m of locations) {
    const mname = m.name || m.main_area;
    if (!mname) continue;
    const ln = mname.toLowerCase();
    if (ln === norm) return { main_area: mname, label: mname, raw: norm };
    if (!bestMain && ln.startsWith(norm)) bestMain = mname;
  }
  for (const m of locations) {
    const mname = m.name || m.main_area;
    if (!mname) continue;
    if (!bestMain && mname.toLowerCase().includes(norm)) bestMain = mname;
  }
  if (bestMain) return { main_area: bestMain, label: bestMain, raw: norm };

  // 2) Sub-area match
  for (const m of locations) {
    const mname = m.name || m.main_area;
    for (const s of (m.sub_areas || [])) {
      const sname = typeof s === "string" ? s : (s?.name || s?.sub_area);
      if (!sname) continue;
      if (sname.toLowerCase().includes(norm)) {
        return { main_area: mname, sub_area: sname, label: `${mname} · ${sname}`, raw: norm };
      }
    }
  }

  return null;
}

// ─── Top-level parser ──────────────────────────────────────────────

export function parseSearchQuery(q, { locations = [], types = [] } = {}) {
  const original = normalize(q);
  if (!original) return null;

  let working = original;
  const params = {};
  const parts = [];

  // 1) BHK
  const bhk = extractBhk(working);
  if (bhk) {
    params.bhk = bhk.value;
    parts.push(bhk.value);
    working = working.replace(new RegExp(bhk.raw, "i"), " ").replace(/\s+/g, " ").trim();
  }

  // 2) Property type
  const type = extractType(working, types);
  if (type) {
    params.property_type = type.value;
    parts.push(type.value);
    working = working.replace(new RegExp(`(^|\\s)${type.raw.replace(/\s+/g, "\\s+")}(?=\\s|$)`, "i"), " ").replace(/\s+/g, " ").trim();
  }

  // 3) Budget
  const budget = extractBudget(working);
  if (budget) {
    if (budget.min != null) params.min_price = budget.min;
    if (budget.max != null) params.max_price = budget.max;
    const lbl = (budget.min != null && budget.max != null)
      ? `${fmtMoney(budget.min)} – ${fmtMoney(budget.max)}`
      : budget.max != null
        ? `Up to ${fmtMoney(budget.max)}`
        : `Above ${fmtMoney(budget.min)}`;
    parts.push(lbl);
    working = working.replace(new RegExp(escapeRegex(budget.raw), "i"), " ").replace(/\s+/g, " ").trim();
  }

  // 4) Location (whatever's left, also strip the common qualifier words)
  working = working
    .replace(/\b(furnished|unfurnished|semi[- ]?furnished|near|close to|by)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  const loc = extractLocation(working, locations);
  if (loc) {
    params.main_area = loc.main_area;
    if (loc.sub_area) params.sub_area = loc.sub_area;
    parts.push(loc.label);
    working = working.replace(new RegExp(escapeRegex(loc.raw), "i"), " ").replace(/\s+/g, " ").trim();
  }

  if (parts.length === 0) return null;

  return {
    params,
    label: parts.join(" · "),
    parts,
    residue: working,
    original,
  };
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function fmtMoney(n) {
  const num = Number(n) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 1)} Cr`;
  if (num >= 100000)   return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)} L`;
  return `₹${num}`;
}

export default parseSearchQuery;
